import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { MyMedicalPaOrdersTableData } from "../../../../data-model/tablesData";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { makePostCallForInitialDataFetch } from "../apis/makePostCallForInitialDataFetch";
import { createFilterPayloadForMyPaOrdersForPostgres } from "../utils/generateFilterPayloadForMyPaOrders";
import { recursivelyGeneratePayloadForInitialFetchForPostgres } from "../utils/recursivelyGeneratePayloadForInitialFetch";
import { MyMedicalPaOrdersState } from "./useMyPaOrdersTableData";
import { useValidFilters } from "./useValidFilters";

/**
 * Orchestrates the initial data fetch for the My PA Orders table. Generates
 * payloads for all pages up to the previously fetched batch (from table context),
 * then triggers the bulk fetch. Re-fetches when toggled via setInitialDataFetchingExecuted(false).
 *
 * @param setMyMedicalPaOrdersState - State setter for the orders loading/data/error state
 * @param setTableData - State setter for the transformed table row data
 * @returns initialDataFetchingExecuted flag and its setter for re-fetch control
 */
export const useInitialDataFetchingForTable = (
  setMyMedicalPaOrdersState: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<MyMedicalPaOrdersTableData[]>
  >,
) => {
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [initialDataFetchingExecuted, setInitialDataFetchingExecuted] =
    useState(false);

  const orgId = getOrgIdForFetchExternalWorklist();

  const { getTableDataFromContext } = useTablesContext();

  const tableAttributes = getTableDataFromContext(
    TableNames.MY_PA_ORDERS_FROM_ALGOLIA,
  );

  const validFilters = useValidFilters();

  useEffect(() => {
    if (
      !initialDataFetchingExecuted &&
      authStatusOptions &&
      authStatusOptions.length > 0
    ) {
      const payload = createFilterPayloadForMyPaOrdersForPostgres(
        validFilters,
        tableAttributes?.searchedText ?? "",
        orgId,
        user?.id ?? "",
        tableAttributes?.sortByField,
        tableAttributes?.sortAscending,
      );
      const payloads = recursivelyGeneratePayloadForInitialFetchForPostgres(
        payload,
        tableAttributes?.lastBatchFetched ?? 0,
        tableAttributes?.sortByField ?? "",
        tableAttributes?.sortAscending ?? true,
      );

      makePostCallForInitialDataFetch(
        payloads,
        authStatusOptions,
        setMyMedicalPaOrdersState,
        setTableData,
        setInitialDataFetchingExecuted,
      );
    }
  }, [authStatusOptions, initialDataFetchingExecuted]);

  return {
    initialDataFetchingExecuted,
    setInitialDataFetchingExecuted,
  };
};
